/* eslint-disable no-nested-ternary */
// TODO remove eslint-disable no-nested-ternary and fix this file
import React from "react";
import { injectIntl } from "react-intl";
import { produce } from "immer";

import { logger } from "@jslib/shared/utils/logger";

import { getSsoContent } from "../../lib/utils/stepComponentHelpers/stepComponentHelpers";
import {
  type VerificationService,
  type StudentPersonalInfoViewModel,
  type ThirdPartyLoopResponse,
  type SSOResponse,
} from "../../lib/types/types";
import { VerificationStepsEnum } from "../../lib/types/runtimeTypes";
import { getMaxReviewTime } from "../../lib/ProgramTheme/programThemeGetters";
import { closeTabRef } from "../../lib/refs/refs";
import { LogoComponent } from "../LogoComponent/LogoComponent";
import { PoweredByComponent } from "../PoweredBy/PoweredByComponent";
import { FormFooterComponent as FormFooter } from "../FormFooter/FormFooterComponent";
import { usePollingInterval } from "../../lib/utils/usePollingInterval/usePollingInterval";
import {
  getPreviousStepOverride,
  getRouteOverride,
} from "../../lib/ServerApi/TestingRouteOverrides";
import { getOptions } from "../../options/options";

import { ReviewPendingComponent } from "./ReviewPendingComponent";
import { CountDownComponent } from "./CountDownComponent";
import { IDCheckLoopPendingComponent } from "./IDCheckLoopPendingComponent";
import { ThirdPartyLoopPendingComponent } from "./ThirdPartyLoopPendingComponent";
import { SingleSignOnPendingComponent } from "./SingleSignOnPendingComponent";

const DOC_UPLOAD_POLL_DURATION_DURING_COUNTDOWN = 5000;
const DOC_UPLOAD_POLL_DURATION = 20000;
const SINGLE_SIGN_ON_POLL_DURATION = 2000;

interface StepPendingProps {
  verificationService: VerificationService;
  intl: any;
}

const calculateShowCountdown = (
  isExplicitlyMockingPending: boolean,
  isPreviousStepSSO: boolean,
  isPreviousStepIdCheckLoop: boolean,
  isPreviousStepThirdPartyLoop: boolean,
  isMockingPreviousStepSSO: boolean,
  isMockingPreviousStepDocUpload: boolean,
  isMockingPreviousStepIdCheckLoop: boolean,
  isMockingPreviousStepThirdPartyLoop: boolean,
) =>
  (isExplicitlyMockingPending ||
    (!isPreviousStepSSO && !isPreviousStepIdCheckLoop && !isPreviousStepThirdPartyLoop)) &&
  !isMockingPreviousStepSSO &&
  !isMockingPreviousStepDocUpload &&
  !isMockingPreviousStepIdCheckLoop &&
  !isMockingPreviousStepThirdPartyLoop;

const StepPending = ({ verificationService, intl }: StepPendingProps) => {
  const { programId } = verificationService;
  const { viewModel } = verificationService;
  const previousViewModel =
    (verificationService.previousViewModel as StudentPersonalInfoViewModel) || undefined;
  const verificationLastResponse =
    verificationService.verificationResponse.lastResponse ||
    verificationService.previousVerificationResponse ||
    undefined;
  const { verificationId } = verificationService.verificationResponse;
  const countdownSeconds = 20;
  const isMockingStep = getRouteOverride();
  const isMockingPreviousStepSSO =
    getPreviousStepOverride() && getPreviousStepOverride() === VerificationStepsEnum.sso;
  const isMockingPreviousStepThirdPartyLoop =
    getPreviousStepOverride() && getPreviousStepOverride() === VerificationStepsEnum.thirdPartyLoop;
  const isMockingPreviousStepIdCheckLoop =
    getPreviousStepOverride() && getPreviousStepOverride() === VerificationStepsEnum.idCheckLoop;
  const isMockingPreviousStepDocUpload =
    getPreviousStepOverride() && getPreviousStepOverride() === VerificationStepsEnum.docUpload;

  const isPreviousStepSSO = (): boolean => {
    if (
      verificationLastResponse &&
      verificationLastResponse.currentStep === VerificationStepsEnum.sso
    ) {
      return true;
    }
    return false;
  };

  const isPreviousStepThirdPartyLoop = (): boolean => {
    if (
      verificationLastResponse &&
      verificationLastResponse.currentStep === VerificationStepsEnum.thirdPartyLoop
    ) {
      return true;
    }
    return false;
  };

  const isPreviousStepIdCheckLoop = (): boolean => {
    if (
      verificationLastResponse &&
      verificationLastResponse.currentStep === VerificationStepsEnum.idCheckLoop
    ) {
      return true;
    }
    return false;
  };

  const [showCountDown, setShowCountDown] = React.useState(
    calculateShowCountdown(
      getOptions().mockStep === "pending",
      isPreviousStepSSO(),
      isPreviousStepIdCheckLoop(),
      isPreviousStepThirdPartyLoop(),
      isMockingPreviousStepSSO,
      isMockingPreviousStepDocUpload,
      isMockingPreviousStepIdCheckLoop,
      isMockingPreviousStepThirdPartyLoop,
    ),
  );

  const pollTime =
    isPreviousStepSSO() || isPreviousStepThirdPartyLoop()
      ? SINGLE_SIGN_ON_POLL_DURATION
      : showCountDown
        ? DOC_UPLOAD_POLL_DURATION_DURING_COUNTDOWN
        : DOC_UPLOAD_POLL_DURATION;

  const stopPollingInterval =
    !isMockingStep &&
    usePollingInterval(() => {
      verificationService.fetchExistingVerificationRequest(
        programId,
        verificationId,
        verificationLastResponse,
        previousViewModel,
        false,
      );
    }, pollTime);

  const cancelSingleSignOnStep = (
    step: VerificationStepsEnum.thirdPartyLoop | VerificationStepsEnum.sso,
  ) => {
    const nextState = produce(viewModel, () => {});
    logger.info("StepSingleSignOnComponent submitting cancel request, step:", step);
    stopPollingInterval();

    closeTabRef();
    verificationService.submitStep(step, nextState, verificationLastResponse);
  };

  const onCountdownFinish = () => {
    setShowCountDown(false);
  };

  const logo = (
    <LogoComponent
      verificationService={verificationService}
      fallbackLogo={
        <img
          className="sid-l-lead-image"
          alt=""
          src="https://assets-resources.sheerid.com/common/images/2018/icons/doc-pending.svg"
        />
      }
    />
  );

  const getPendingContent = () => {
    const showSSO = isPreviousStepSSO() || isMockingPreviousStepSSO;
    const showThirdPartyLoop =
      isPreviousStepThirdPartyLoop() || isMockingPreviousStepThirdPartyLoop;
    const showIdCheckLoop = isPreviousStepIdCheckLoop() || isMockingPreviousStepIdCheckLoop;

    if (showCountDown) {
      return (
        <CountDownComponent
          logo={logo}
          onCountdownFinish={onCountdownFinish}
          countdownSeconds={countdownSeconds}
        />
      );
    }

    if (showSSO) {
      const lastResponse = verificationLastResponse as SSOResponse;
      const { isReclaim, messages } = getSsoContent(lastResponse);

      return (
        <SingleSignOnPendingComponent
          isReclaim={isReclaim}
          stepName={VerificationStepsEnum.sso}
          messages={messages}
          logo={logo}
          cancelSingleSignOn={() => cancelSingleSignOnStep(VerificationStepsEnum.sso)}
        />
      );
    }

    if (showThirdPartyLoop) {
      return (
        <ThirdPartyLoopPendingComponent
          logo={logo}
          cancelThirdPartyLoop={() => cancelSingleSignOnStep(VerificationStepsEnum.thirdPartyLoop)}
          verificationLastResponse={verificationLastResponse as ThirdPartyLoopResponse}
          isMockingPreviousStepThirdPartyLoop={isMockingPreviousStepThirdPartyLoop}
        />
      );
    }

    if (showIdCheckLoop) {
      return <IDCheckLoopPendingComponent logo={logo} />;
    }

    return (
      <ReviewPendingComponent
        verificationService={verificationService}
        logo={logo}
        intl={intl}
        maxReviewTime={getMaxReviewTime(verificationService.programTheme)}
      />
    );
  };

  return (
    <div id="sid-step-pending" className="sid-l-container">
      <div role="main">{getPendingContent()}</div>
      {showCountDown || isPreviousStepIdCheckLoop() || isMockingPreviousStepIdCheckLoop ? (
        <PoweredByComponent verificationService={verificationService} isCentered />
      ) : (
        <FormFooter verificationService={verificationService}>
          <div /> {/* suppress default */}
        </FormFooter>
      )}
    </div>
  );
};

export const StepPendingComponent = injectIntl(StepPending);
